/**
 * CloudDemoPage — V2 Demo Panel for the Cloud 5.0 platform.
 *
 * 3 sections:
 *   1. Device & Event Configuration Card — 6 cascading dropdowns in 2-col grid + action buttons
 *   2. Escalation Simulator Card — simulator settings + Simulate button + scrollable results
 *   3. Cloud Emergency Alert Table — real-time alerts from NotificationContext with Clear action
 *
 * NotificationContext integration for real-time cloud emergency alerts (5.0 snake_case schema).
 *
 * Key differences from 4.0 DemoPage:
 *   - Uses `activeCloudAlarms` (not `activeAlarms`)
 *   - Event Category + Event Type cascading dropdowns (type filtered by selected category)
 *   - Server from DB (`core_palcare_devices` where palcare_interface_id === 3)
 *   - v4 alarm endpoints (emergency vs non-emergency via `isEmergency()`)
 *   - Full 5.0 payload schema via `buildCloudAlarmPayload`
 *   - Groups loaded from DB for `group_ids` in payload
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import NavigationV2 from '../../components/v2/NavigationV2.jsx';
import Toast from '../../components/v2/Toast.jsx';
import useToast from '../../hooks/useToast.js';
import { useNotifications } from '../../contexts/NotificationContext.jsx';
import {
    getSession,
    getCampusId,
    getCampusList,
    updateSessionCampus,
    getUserId,
    getUsername,
    getCampusConfig,
    getCampusTzOffset,
} from '../../services/session.js';
import {
    sortFunc, getLocalTimeWithOffset, getLuxonUTCTime, convertHHMMtoSec,
} from '../../libs.jsx';

// Reuse shared fetches from 4.0 demoApi
import {
    fetchResidentsAndDevices, fetchAreasAndDevices, fetchEventCategories,
    createRounds, createCheckin, simulateCareflow, buildRoundsPayload,
} from './api/demoApi.js';

// Cloud-specific API functions
import {
    fetchEventTypes, fetchPalcareDevices, fetchGroups,
    createCloudAlarm, clearCloudAlarm,
    buildCloudAlarmPayload, buildClearPayload,
} from './api/cloudDemoApi.js';

import CloudDeviceSelectionCard from './components/CloudDeviceSelectionCard.jsx';
import CloudSimulatorCard from './components/CloudSimulatorCard.jsx';
import MobilePhoneFrame from './components/MobilePhoneFrame.jsx';
import MobileAlertsView from '../../components/mobile-alerts/MobileAlertsView.jsx';
import MobileReasonDrawer from '../../components/mobile-alerts/MobileReasonDrawer.jsx';
import AlertSyncIndicator from '../events/alerts/components/AlertSyncIndicator.jsx';
import { acceptAlert, finishAlert } from '../events/alerts/api/cloudAlertsApi.js';
import { fetchReasonCodes, fetchReasonCodeTypes, fetchHighPriorityEntities } from '../settings/system-settings/api/cloudSettingsApi.js';

import './demo-page.css';

const CloudDemoPage = () => {
    // ======================================================================
    // SESSION
    // ======================================================================
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
    const isCloud = Boolean(campusConfig?.is_cloud);
    const tzOffset = getCampusTzOffset(campusId);

    // ======================================================================
    // NOTIFICATION CONTEXT (real-time cloud emergency alerts, 5.0 schema)
    // ======================================================================
    const {
        activeCloudAlarms, isConnected, connectionError, isDataStale,
        registerAlertViewer, unregisterAlertViewer, resubscribeToCurrentCampus,
    } = useNotifications();

    useEffect(() => {
        registerAlertViewer();
        return () => unregisterAlertViewer();
    }, [registerAlertViewer, unregisterAlertViewer]);

    // ======================================================================
    // STATE — Reference data
    // ======================================================================
    const [residentData, setResidentData] = useState([]);
    const [areaData, setAreaData] = useState([]);
    const [eventCatData, setEventCatData] = useState([]);
    const [eventTypeData, setEventTypeData] = useState([]);
    const [serverData, setServerData] = useState([]);
    const [groupData, setGroupData] = useState([]);

    // ======================================================================
    // STATE — Device & Event Selection (cascading)
    // ======================================================================
    const [dataType, setDataType] = useState('');
    const [entityIndex, setEntityIndex] = useState('');
    const [deviceIndex, setDeviceIndex] = useState('');
    const [server, setServer] = useState('');
    const [eventCatId, setEventCatId] = useState(1);
    const [eventCatName, setEventCatName] = useState('Emergency');
    const [eventTypeId, setEventTypeId] = useState(1);
    const [eventTypeName, setEventTypeName] = useState('Emergency');

    // ======================================================================
    // STATE — Simulator Config
    // ======================================================================
    const [duration, setDuration] = useState(30);
    const [timeOfDay, setTimeOfDay] = useState('');
    const [simulateData, setSimulateData] = useState([]);

    // ======================================================================
    // STATE — Loading / Clearing
    // ======================================================================
    const [loadingAlarm, setLoadingAlarm] = useState(false);
    const [loadingRounds, setLoadingRounds] = useState(false);
    const [loadingCheckin, setLoadingCheckin] = useState(false);
    const [loadingSimulate, setLoadingSimulate] = useState(false);
    const [clearingEventId, setClearingEventId] = useState(null);

    // ======================================================================
    // STATE — Portal Accept/Finish (always on for demo, not gated by portal_afr)
    // ======================================================================
    const [reasonCodes, setReasonCodes] = useState([]);
    const [reasonCodeTypes, setReasonCodeTypes] = useState([]);
    const [pendingEventId, setPendingEventId] = useState(null);
    const [finishTarget, setFinishTarget] = useState(null);

    // High-priority event types (config) — Set of event_type_id (strings) flagged
    // high priority for this campus. Drives the mobile preview's pin-to-top + banner.
    const [highPriorityTypeIds, setHighPriorityTypeIds] = useState(() => new Set());

    // ======================================================================
    // STATE — Clear-alert selector (segmented sub-section in device card)
    // ======================================================================
    const [clearEventId, setClearEventId] = useState('');

    // ======================================================================
    // TOAST
    // ======================================================================
    const { toast, showToast, dismissToast } = useToast(6000);

    // ======================================================================
    // DATA LOADING — 6 fetches: residents, areas, event cats, event types,
    //                palcare devices (servers), groups
    // ======================================================================
    const loadData = useCallback(async () => {
        if (!campusId) return;
        try {
            const [residents, areas, categories, types, devices, groups] = await Promise.all([
                fetchResidentsAndDevices(campusId),
                fetchAreasAndDevices(campusId),
                fetchEventCategories(campusId),
                fetchEventTypes(campusId),
                fetchPalcareDevices(campusId),
                fetchGroups(campusId),
            ]);
            setResidentData(Array.isArray(residents) ? residents : []);
            setAreaData(Array.isArray(areas) ? areas : []);
            const cats = Array.isArray(categories) ? categories : [];
            setEventCatData(cats);
            const tps = Array.isArray(types) ? types : [];
            setEventTypeData(tps);
            setServerData(Array.isArray(devices) ? devices : []);
            setGroupData(Array.isArray(groups) ? groups : []);
        } catch (err) {
            console.error('[CloudDemoPage] loadData error:', err);
        }
    }, [campusId]);

    useEffect(() => { loadData(); }, [loadData]);

    // Re-poll every 20 minutes (matching V1's 1200s interval)
    useEffect(() => {
        const timer = setInterval(loadData, 1200000);
        return () => clearInterval(timer);
    }, [loadData]);

    // ======================================================================
    // RESET on campusId change
    // ======================================================================
    useEffect(() => {
        setDataType('');
        setEntityIndex('');
        setDeviceIndex('');
        setSimulateData([]);
    }, [campusId]);

    // ======================================================================
    // SET DEFAULT SERVER when serverData loads
    // ======================================================================
    useEffect(() => {
        const servers = serverData.filter((s) => s.palcare_interface_id === 3);
        if (servers.length > 0) {
            setServer(servers[0].palcare_device_id.toString());
        } else {
            setServer('');
        }
    }, [serverData]);

    // ======================================================================
    // DERIVED VALUES
    // ======================================================================
    const serverOptions = useMemo(() => {
        return serverData
            .filter((s) => s.palcare_interface_id === 3)
            .map((s) => ({
                value: s.palcare_device_id.toString(),
                label: `${s.description} (${s.mac_addr})`,
            }));
    }, [serverData]);

    /** Sorted entity list — this is the single source of truth for entity indices */
    const sortedEntities = useMemo(() => {
        if (!dataType) return [];
        const src = dataType === 'resident' ? residentData : areaData;
        const sortKey = dataType === 'resident' ? 'resident_first_name' : 'area_name';
        return [...src].sort((a, b) => sortFunc(a, b, sortKey, 'asc'));
    }, [dataType, residentData, areaData]);

    const entityOptions = useMemo(() => {
        const withDevices = [];
        const withoutDevices = [];
        sortedEntities.forEach((item, i) => {
            const label = dataType === 'resident'
                ? `${item.resident_first_name} ${item.resident_last_name}`
                : item.area_name;
            if (item.devices?.length > 0) {
                withDevices.push({ value: i.toString(), label, disabled: false });
            } else {
                withoutDevices.push({ value: i.toString(), label: `${label} (NO DEVICES)`, disabled: true });
            }
        });
        return [...withDevices, ...withoutDevices];
    }, [dataType, sortedEntities]);

    /** The selected entity from the sorted list */
    const selectedEntity = useMemo(() => {
        if (entityIndex === '') return null;
        return sortedEntities[parseInt(entityIndex, 10)] || null;
    }, [sortedEntities, entityIndex]);

    /** Sorted devices for the selected entity — single source of truth for device indices */
    const sortedDevices = useMemo(() => {
        if (!selectedEntity?.devices?.length) return [];
        return [...selectedEntity.devices].sort((a, b) => sortFunc(a, b, 'device_name', 'asc'));
    }, [selectedEntity]);

    const deviceOptions = useMemo(() => {
        return sortedDevices.map((d, i) => ({
            value: i.toString(),
            label: `${d.device_name} (${d.manufacture_uuid || ''})`,
        }));
    }, [sortedDevices]);

    const selectedDevice = useMemo(() => {
        if (deviceIndex === '') return null;
        return sortedDevices[parseInt(deviceIndex, 10)] || null;
    }, [sortedDevices, deviceIndex]);

    const isAioDevice = useMemo(() =>
        selectedDevice?.device_name?.toLowerCase().includes('aio') || false,
    [selectedDevice]);

    const canSubmit = Boolean(selectedDevice);

    const sortedEventCats = useMemo(() =>
        [...(eventCatData || [])].sort((a, b) => sortFunc(a, b, 'event_category_name', 'asc')),
    [eventCatData]);

    const filteredEventTypes = useMemo(() =>
        [...(eventTypeData || [])]
            .filter((t) => t.event_category_id === eventCatId)
            .sort((a, b) => sortFunc(a, b, 'event_type_name', 'asc')),
    [eventTypeData, eventCatId]);

    const groupList = useMemo(() =>
        (groupData || []).map((g) => g.group_id),
    [groupData]);

    /** Active (uncleared) alerts for the phone emulator and the Clear dropdown. */
    const activeAlertsForClear = useMemo(() => {
        return (activeCloudAlarms || [])
            .filter((a) => !a.ack_datetime_utc)
            .slice()
            .sort((a, b) => sortFunc(a, b, 'event_datetime_utc', 'asc'));
    }, [activeCloudAlarms]);

    // If the selected clear target disappears (already cleared elsewhere), reset.
    useEffect(() => {
        if (!clearEventId) return;
        const stillExists = activeAlertsForClear.some((a) => String(a.event_id) === String(clearEventId));
        if (!stillExists) setClearEventId('');
    }, [activeAlertsForClear, clearEventId]);

    // ======================================================================
    // HANDLERS — Cascading resets
    // ======================================================================
    const handleDataTypeChange = useCallback((val) => {
        setDataType(val);
        setEntityIndex('');
        setDeviceIndex('');
    }, []);

    const handleEntityChange = useCallback((val) => {
        setEntityIndex(val);
        setDeviceIndex('');
    }, []);

    const handleEventCatChange = useCallback((catId) => {
        setEventCatId(catId);
        // Resolve category name from data
        const cat = eventCatData.find((c) => c.event_category_id === catId);
        setEventCatName(cat?.event_category_name || '');
        // Reset event type to first matching type
        const types = (eventTypeData || []).filter((t) => t.event_category_id === catId);
        if (types.length > 0) {
            setEventTypeId(types[0].event_type_id);
            setEventTypeName(types[0].event_type_name);
        } else {
            setEventTypeId(0);
            setEventTypeName('');
        }
    }, [eventCatData, eventTypeData]);

    const handleEventTypeChange = useCallback((typeId) => {
        setEventTypeId(typeId);
        const type = eventTypeData.find((t) => t.event_type_id === typeId);
        setEventTypeName(type?.event_type_name || '');
    }, [eventTypeData]);

    // ======================================================================
    // HANDLERS — Campus
    // ======================================================================
    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        const cfg = session?.campusData?.[val.toString()];
        // Cloud → non-cloud: reload so Demo.jsx dispatcher picks DemoPage
        if (!cfg?.is_cloud && isCloud) {
            updateSessionCampus(val);
            window.location.reload();
            return;
        }
        setCampusId(val);
        updateSessionCampus(val);
        resubscribeToCurrentCampus();
    }, [session, isCloud, resubscribeToCurrentCampus]);

    // ======================================================================
    // HANDLERS — Actions
    // ======================================================================
    const handleCreateAlarm = useCallback(async () => {
        if (!selectedDevice || !selectedEntity) return;
        setLoadingAlarm(true);
        const payload = buildCloudAlarmPayload({
            dataType,
            resident: dataType === 'resident' ? selectedEntity : null,
            area: dataType === 'area' ? selectedEntity : null,
            device: selectedDevice,
            serverId: parseInt(server, 10) || 0,
            eventCatId,
            eventCatName,
            eventTypeId,
            eventTypeName,
            session,
            campusId,
            residentData,
            groupList,
        });
        const result = await createCloudAlarm(payload, eventCatId);
        setLoadingAlarm(false);
        if (result[0]) {
            showToast('success', `Alert created for ${selectedDevice.device_name}`);
        } else {
            showToast('error', `Failed to create alarm for ${selectedDevice.device_name}`);
        }
    }, [selectedDevice, selectedEntity, dataType, residentData, server,
        eventCatId, eventCatName, eventTypeId, eventTypeName,
        session, campusId, groupList, showToast]);

    const handleRounds = useCallback(async () => {
        if (!selectedDevice || !selectedEntity || dataType !== 'area') return;
        setLoadingRounds(true);
        const v4Payload = buildCloudAlarmPayload({
            dataType: 'area', resident: null, area: selectedEntity, device: selectedDevice,
            serverId: parseInt(server, 10) || 0,
            eventCatId, eventCatName, eventTypeId, eventTypeName,
            session, campusId, residentData, groupList,
        });
        const payload = buildRoundsPayload({
            deviceId: selectedDevice.device_id,
            residentId: v4Payload.residents?.[0]?.resident_id,
            areaId: selectedEntity.area_id,
            campusId,
            tzOffset,
        });
        const result = await createRounds(payload, campusId, session?.user, session?.sessionId);
        setLoadingRounds(false);
        if (result[0]) {
            showToast('success', `Rounds triggered with ${selectedDevice.device_name} in ${selectedEntity.area_name}`);
        } else {
            showToast('error', `Failed to create rounds with ${selectedDevice.device_name}`);
        }
    }, [selectedDevice, selectedEntity, dataType, server,
        eventCatId, eventCatName, eventTypeId, eventTypeName,
        session, campusId, residentData, groupList, tzOffset, showToast]);

    const handleCheckin = useCallback(async () => {
        if (!selectedDevice || !selectedEntity || dataType !== 'area') return;
        setLoadingCheckin(true);
        const v4Payload = buildCloudAlarmPayload({
            dataType: 'area', resident: null, area: selectedEntity, device: selectedDevice,
            serverId: parseInt(server, 10) || 0,
            eventCatId, eventCatName, eventTypeId, eventTypeName,
            session, campusId, residentData, groupList,
        });
        const payload = buildRoundsPayload({
            deviceId: selectedDevice.device_id,
            residentId: v4Payload.residents?.[0]?.resident_id,
            areaId: selectedEntity.area_id,
            campusId,
            tzOffset,
        });
        const result = await createCheckin(payload, campusId, session?.user, session?.sessionId);
        setLoadingCheckin(false);
        if (result[0]) {
            const resName = v4Payload.residents?.[0];
            showToast('success', `${resName?.resident_name || 'Resident'} Checked in.`);
        } else {
            showToast('error', 'Failed to check-in');
        }
    }, [selectedDevice, selectedEntity, dataType, server,
        eventCatId, eventCatName, eventTypeId, eventTypeName,
        session, campusId, residentData, groupList, tzOffset, showToast]);

    const handleSimulate = useCallback(async () => {
        if (!selectedDevice) return;
        setLoadingSimulate(true);
        setSimulateData([]);
        const timeofday = timeOfDay ? convertHHMMtoSec(timeOfDay) : undefined;
        const data = await simulateCareflow({
            campusId,
            deviceId: selectedDevice.device_id,
            timeofday,
            runtime: duration,
            eventCategoryId: eventCatId
        });
        setSimulateData(data || []);
        setLoadingSimulate(false);
    }, [selectedDevice, campusId, timeOfDay, duration, eventCatId]);

    // ======================================================================
    // REASON CODES — loaded once per campus (always on for demo page)
    // ======================================================================
    useEffect(() => {
        if (!campusId) {
            setReasonCodes([]);
            setReasonCodeTypes([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const [codes, types] = await Promise.all([
                    fetchReasonCodes(campusId),
                    fetchReasonCodeTypes(campusId),
                ]);
                if (!cancelled) {
                    setReasonCodes(Array.isArray(codes) ? codes : []);
                    setReasonCodeTypes(Array.isArray(types) ? types : []);
                }
            } catch (err) {
                console.error('[CloudDemoPage] fetchReasonCodes error:', err);
                if (!cancelled) {
                    setReasonCodes([]);
                    setReasonCodeTypes([]);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [campusId]);

    // ======================================================================
    // HIGH-PRIORITY EVENT TYPES — config data, loaded once per campus (mirrors
    // CloudAlertsPage). Kept as a Set of event_type_id for the mobile preview.
    // ======================================================================
    useEffect(() => {
        if (!campusId) {
            setHighPriorityTypeIds(new Set());
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchHighPriorityEntities(campusId);
                if (cancelled) return;
                const ids = (Array.isArray(data) ? data : [])
                    .map((r) => (r.event_type_id ?? '').toString())
                    .filter((v) => v !== '' && v !== 'null' && v !== 'undefined');
                setHighPriorityTypeIds(new Set(ids));
            } catch (err) {
                console.error('[CloudDemoPage] fetchHighPriorityEntities error:', err);
                if (!cancelled) setHighPriorityTypeIds(new Set());
            }
        })();
        return () => { cancelled = true; };
    }, [campusId]);

    // ======================================================================
    // ACCEPT / FINISH HANDLERS
    // ======================================================================
    const handleAccept = useCallback(async (row) => {
        if (!row?.event_id || pendingEventId) return;
        setPendingEventId(row.event_id);
        try {
            const [ok, errMsg] = await acceptAlert({
                campusId: row.campus_id ?? campusId,
                eventId: row.event_id,
                userId: getUserId(),
                username: getUsername(),
                displayName: session?.display || getUsername(),
            });
            if (ok) {
                showToast('success', 'Alert accepted.');
            } else {
                showToast('error', errMsg || 'Failed to accept alert.');
            }
        } finally {
            setPendingEventId(null);
        }
    }, [pendingEventId, campusId, session, showToast]);

    const handleOpenFinish = useCallback((row) => {
        if (!row?.event_id || pendingEventId) return;
        setFinishTarget(row);
    }, [pendingEventId]);

    const handleCancelFinish = useCallback(() => {
        setFinishTarget(null);
    }, []);

    const handleSubmitFinish = useCallback(async (reasonId, residentId) => {
        const row = finishTarget;
        if (!row?.event_id || !reasonId) return;
        setPendingEventId(row.event_id);
        try {
            const [ok, errMsg] = await finishAlert({
                campusId: row.campus_id ?? campusId,
                eventId: row.event_id,
                reasonId,
                // single resident → implicit; multiple → user-selected; none → 0
                residentId: residentId ?? row.residents?.[0]?.resident_id ?? 0,
            });
            if (ok) {
                setFinishTarget(null);
                showToast('success', 'Alert finished.');
            } else {
                showToast('error', errMsg || 'Failed to finish alert.');
            }
        } finally {
            setPendingEventId(null);
        }
    }, [finishTarget, campusId, showToast]);

    const handleClearAlarm = useCallback(async (eventId) => {
        setClearingEventId(eventId);
        const event = (activeCloudAlarms || []).find((a) => String(a.event_id) === String(eventId));
        const catId = event?.event_category_id || eventCatId;
        const serialNum = event?.manufacture_uuid || "";
        const payload = buildClearPayload(campusId, eventId, serialNum);
        // Use the event's category to route to correct endpoint
        // Find the event in activeCloudAlarms to get its category
        const result = await clearCloudAlarm(payload, catId);
        setClearingEventId(null);
        if (result[0]) {
            setClearEventId('');
            showToast('success', 'Event is cleared.');
        } else {
            showToast('error', 'Failed to clear event');
        }
    }, [campusId, activeCloudAlarms, eventCatId, showToast]);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="dm-page">
                    {/* Toast */}
                    <Toast toast={toast} onDismiss={dismissToast} />

                    {/* Header */}
                    <div className="na-header">
                        <div className="na-header__left">
                            <h1 className="na-header__title">Demo Panel (5.0)</h1>
                            <AlertSyncIndicator isConnected={isConnected} isDataStale={isDataStale} connectionError={connectionError} />
                        </div>
                        <div className="na-header__right">
                            <div className="ps-campus-row">
                                <label htmlFor="dmc-campus" className="ps-campus-label">Campus:</label>
                                {campusList.length > 1 ? (
                                    <select id="dmc-campus" className="ps-campus-select" value={campusId} onChange={handleCampusChange}>
                                        {campusList.map((c) => (
                                            <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <span className="ps-campus-name">{campusConfig?.name || ''}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Content — phone emulator on the LEFT, config + simulator on the RIGHT */}
                    <div className="dm-content">
                        <div className="dm-split">
                            <div className="dm-split__left">
                                <h3 className="dm-section-title">Mobile App Preview</h3>
                                <MobilePhoneFrame>
                                    <MobileAlertsView
                                        alerts={activeCloudAlarms || []}
                                        sessionUserId={getUserId()}
                                        sessionUsername={getUsername()}
                                        onAccept={handleAccept}
                                        onFinish={handleOpenFinish}
                                        pendingEventId={pendingEventId}
                                        highPriorityTypeIds={highPriorityTypeIds}
                                    />
                                    <MobileReasonDrawer
                                        open={Boolean(finishTarget)}
                                        row={finishTarget}
                                        reasonCodes={reasonCodes}
                                        reasonCodeTypes={reasonCodeTypes}
                                        saving={finishTarget ? pendingEventId === finishTarget.event_id : false}
                                        onSubmit={handleSubmitFinish}
                                        onCancel={handleCancelFinish}
                                    />
                                </MobilePhoneFrame>
                            </div>
                            <div className="dm-split__right">
                                <CloudDeviceSelectionCard
                                    dataType={dataType}
                                    onDataTypeChange={handleDataTypeChange}
                                    entityOptions={entityOptions}
                                    entityIndex={entityIndex}
                                    onEntityChange={handleEntityChange}
                                    entityLabel={dataType === 'resident' ? 'Resident' : dataType === 'area' ? 'Area' : 'Entity'}
                                    deviceOptions={deviceOptions}
                                    deviceIndex={deviceIndex}
                                    onDeviceChange={setDeviceIndex}
                                    serverOptions={serverOptions}
                                    server={server}
                                    onServerChange={setServer}
                                    eventCatOptions={sortedEventCats}
                                    eventCatId={eventCatId}
                                    onEventCatChange={handleEventCatChange}
                                    eventTypeOptions={filteredEventTypes}
                                    eventTypeId={eventTypeId}
                                    onEventTypeChange={handleEventTypeChange}
                                    canSubmit={canSubmit}
                                    isAioDevice={isAioDevice}
                                    loadingAlarm={loadingAlarm}
                                    loadingRounds={loadingRounds}
                                    loadingCheckin={loadingCheckin}
                                    onCreateAlarm={handleCreateAlarm}
                                    onRounds={handleRounds}
                                    onCheckin={handleCheckin}
                                    activeAlarms={activeAlertsForClear}
                                    clearEventId={clearEventId}
                                    onClearEventIdChange={setClearEventId}
                                    onClearAlarm={handleClearAlarm}
                                    clearingEventId={clearingEventId}
                                />
                                <CloudSimulatorCard
                                    duration={duration}
                                    onDurationChange={setDuration}
                                    timeOfDay={timeOfDay}
                                    onTimeOfDayChange={setTimeOfDay}
                                    onSimulate={handleSimulate}
                                    canSubmit={canSubmit}
                                    simulateData={simulateData}
                                    loadingSimulate={loadingSimulate}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CloudDemoPage;
